import { GraphModel } from 'app/model/graphmodel';
import { Injectable, InjectionToken, Inject } from '@angular/core';
import type { AccountInfo } from '@azure/msal-common';
import { BehaviorSubject } from 'rxjs';

import { IResourceContext, RESOURCE_CONTEXT_DATA_TOKEN } from 'app/multi-contexts/resource-context.typings';
import { Http } from 'app/services/http';
import { GlobalVariables } from 'app/services/globals';
import { AuthConstants } from './auth-constants';
import { IAuthProvider } from 'app/interfaces/IAuthProvider';
import { FactoryUrlInformationService } from '../factory-url-information.service';
import { WorkspaceUrlInformationService } from '../workspace-url-information.service';
import { isFairfaxEnvironment, isMooncakeEnvironment, isBleuEnvironment, isDelosEnvironment } from 'environments/environment.util';
import { UrlInformationBaseService } from '../url-information-base.service';
import { I_LOGGER, ILogger } from 'app/logger/ILogger';
import { IRoleAssginment } from 'app/interfaces/IRoleAssignment';
import { IUserInfo } from 'app/interfaces/IAuthenticationContext';
import { Msal2AuthProvider } from './msal2.0-provider';
import { assertPositive } from 'app/utils/assertUtils';
import { GrantAuthDialogService } from 'app/services/authentication/grant-auth-dialog/grant-auth-dialog.service';

export const TENANT_ID = new InjectionToken<string>('tenantId');

@Injectable({
    providedIn: 'root'
})
export class AuthService implements IAuthProvider {
    private _authProvider: IAuthProvider;
    private _urlInfo: UrlInformationBaseService;

    public get fetchTenantAsync(): Promise<string> {
        return this._authProvider.fetchTenantAsync;
    }

    public get userPhotoData$(): BehaviorSubject<string> {
        return this._authProvider.userPhotoData$;
    }

    public get fetchUserRoleAsync(): Promise<IRoleAssginment[]> {
        return this._authProvider.fetchUserRoleAsync;
    }

    public get fetchUserRoleResolver(): (value: IRoleAssginment[]) => void {
        return this._authProvider.fetchUserRoleResolver;
    }

    public constructor(
        @Inject(RESOURCE_CONTEXT_DATA_TOKEN) contextData: IResourceContext,
        private readonly httpClient: Http,
        @Inject(I_LOGGER) private readonly _logger: ILogger,
        private readonly dfUrlInfo: FactoryUrlInformationService,
        private readonly wsUrlInfo: WorkspaceUrlInformationService,
        private readonly _grantAuthDialogService: GrantAuthDialogService
    ) {
        if (multiContextRootModuleAsEntry) {
            assertPositive(
                contextData.contextType !== 'root',
                'AuthService could only be initialized in the non-root context.'
            );
        }

        this._urlInfo = __IS_A365__ ? this.wsUrlInfo : this.dfUrlInfo;
    }

    /**
     * This method is called in `app.module`, `APP_INITIALIZER`
     * @param authProvider
     */
    public setAuthProvider(authProvider: IAuthProvider): void {
        this._authProvider = authProvider;
    }

    /**
     * @deprecated Deprecating this func, to keep auth provider singleton
     * @param tenantId
     * @returns Auth provider instance
     */
    public async getAuthProviderForTenant(tenantId?: string): Promise<IAuthProvider> {
        const isFabricEnvironment = __IS_FABRIC_DPE__ || __IS_FABRIC_ADF__;
        if (!tenantId || (GlobalVariables.enableIFrameMode && !isFabricEnvironment)) {
            return this._authProvider;
        } else if (isFabricEnvironment) {
            return GlobalVariables.enableMountDfTenantConfiguration
                ? this.createTenantScopedAuthProvider(tenantId)
                : this._authProvider;
        } else {
            // let's monitor the usage for a while before removing this func
            this._logger.log('AuthService', 'unexpected usage of getAuthProviderForTenant', {
                loggedInTenantId: this._authProvider?.getCurrentUserTenant(),
                newTenantId: tenantId,
                callStack: new Error().stack
            });
            const cloudDiscoveryMetadata =
                isFairfaxEnvironment() || isMooncakeEnvironment() || isBleuEnvironment() || isDelosEnvironment()
                    ? await this.getCloudDiscoveryMetadata(tenantId)
                    : undefined;
            return new Msal2AuthProvider(
                this.httpClient,
                this._logger,
                this._urlInfo,
                this._grantAuthDialogService,
                tenantId,
                false,
                {
                    cloudDiscoveryMetadata
                }
            );
        }
    }

    public clearCache(): void {
        return this._authProvider.clearCache();
    }

    public navigateToCorrectEnv(url: string): void {
        this._authProvider.isNavigatingBetweenInternalAndExternal = true;
        window.location.replace(url);
    }

    public handleLogin(): void {
        return this._authProvider.handleLogin();
    }

    public loginRedirect(hint: string): void {
        return this._authProvider.loginRedirect(hint);
    }

    public loginPopUp(tenantId?: string): Promise<AccountInfo> {
        return this._authProvider.loginPopUp(tenantId);
    }

    public async getAllLoggedinUsers(): Promise<IUserInfo[]> {
        const loggedInUsers = await this._authProvider.getAllLoggedinUsers?.();
        if (Array.isArray(loggedInUsers)) {
            return loggedInUsers;
        }

        const accounts = await this.getAllAccounts();
        return accounts.map((account) => ({
            userName: account.username,
            profile: account.idTokenClaims
        }));
    }

    public async getAllAccounts(): Promise<AccountInfo[]> {
        const accounts = await this._authProvider.getAllAccounts?.();
        return Array.isArray(accounts) ? accounts : [];
    }

    public async logOutByAccountName(userName: string): Promise<void> {
        return this._authProvider.logOutByAccountName(userName);
    }

    public async getTenant(subId: string): Promise<string> {
        return this._authProvider.getTenant(subId);
    }

    public getCachedUser(): IUserInfo {
        return this._authProvider.getCachedUser();
    }

    public getUserDisplayName(): string {
        return this._authProvider.getUserDisplayName();
    }

    public getCurrentUserTenant(): string {
        return this._authProvider.getCurrentUserTenant();
    }

    public isGuestUser(): Promise<boolean> {
        return this._authProvider.isGuestUser();
    }

    public async getToken(
        authArea: string | string[] = AuthConstants.AUTH_AREA,
        disableRedirection: boolean = false,
        msalAccount?: AccountInfo,
        tenantId?: string,
        ignoreTokenFailure?: boolean,
        forceRefresh?: boolean
    ): Promise<string> {
        return this._authProvider.getToken(authArea, disableRedirection, msalAccount, tenantId, ignoreTokenFailure, forceRefresh);
    }

    public async fetchPhotoFromGraphAPI(user?: string): Promise<string> {
        return this._authProvider.fetchPhotoFromGraphAPI(user);
    }

    public async fetchUserInfo(user: string): Promise<GraphModel.IAADObject> {
        return this._authProvider.fetchUserInfo(user);
    }

    public async getUserPhoto(): Promise<void> {
        return this._authProvider.getUserPhoto();
    }

    public logOut(): void {
        return this._authProvider.logOut();
    }

    public async isWorkspaceAdmin(): Promise<boolean> {
        const userRoles = (await this.fetchUserRoleAsync) || [];
        const userRoleNames = userRoles.map((r) => r.RoleName);
        return userRoleNames.includes('WorkspaceAdmin');
    }

    public getUserID(): string {
        return this._authProvider.getUserID();
    }

    public getUserName(): string {
        return this._authProvider.getUserName();
    }

    public switchAccount(): void {
        return this._authProvider.switchAccount();
    }

    public async isLoggedIn(): Promise<boolean> {
        return this._authProvider.isLoggedIn();
    }

    public async getCloudDiscoveryMetadata(tenantId: string): Promise<string> {
        const metadata = await fetch(
            `${AuthConstants.AAD_ENDPOINT}common/discovery/instance?api-version=1.1&authorization_endpoint=${
                AuthConstants.AAD_ENDPOINT
            }${tenantId || 'organizations'}/oauth2/v2.0/authorize`
        )
            .then((result) => result.json())
            .catch(() => undefined);
        return JSON.stringify(metadata);
    }

    private createTenantScopedAuthProvider(scopedTenantId: string): IAuthProvider {
        const sharedProvider = this._authProvider;
        return new Proxy<IAuthProvider>(sharedProvider, {
            get(target, property): unknown {
                if (property === 'getToken') {
                    return (
                        authArea?: string | string[],
                        disableRedirection?: boolean,
                        msalAccount?: AccountInfo,
                        tenantId?: string,
                        ignoreTokenFailure?: boolean,
                        forceRefresh?: boolean
                    ): Promise<string> => target.getToken(
                        authArea,
                        disableRedirection,
                        msalAccount,
                        tenantId ?? scopedTenantId,
                        ignoreTokenFailure,
                        forceRefresh
                    );
                }

                const value = Reflect.get(target, property, target) as unknown;
                return typeof value === 'function' ? value.bind(target) : value;
            }
        });
    }
}
